// 🌐 API Client — habla DIRECTO a PostgREST (arquitectura PostgREST-only)
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const POSTGREST_URL = import.meta.env.VITE_POSTGREST_URL || 'http://localhost:3999';

// -----------------------------------------------------
// Almacén de tokens (Bearer). PostgREST lee el JWT del header Authorization.
//
// Aislado POR PESTAÑA (sessionStorage): dos pestañas del mismo origen pueden
// tener sesiones/empresas distintas sin pisarse, y cada una sobrevive su F5.
// localStorage se usa solo como SEMILLA: una pestaña NUEVA (sessionStorage
// vacío) hereda la última sesión conocida por comodidad; una pestaña ya
// activa nunca se re-siembra.
// -----------------------------------------------------
const ACCESS_KEY = 'erp_access_token';
const REFRESH_KEY = 'erp_refresh_token';
// «Mantener la sesión en este dispositivo» (TrackConstruction): '1' = la semilla persiste en
// localStorage (la app reabre con sesión, también sin señal); '0' = solo esta pestaña (tableta
// compartida: al cerrar la app, la sesión termina). Sin elección explícita rige '1', que es el
// comportamiento de siempre del ERP.
const RECORDAR_KEY = 'erp_recordar';

function getWithSeed(key: string): string | null {
  const fromSession = sessionStorage.getItem(key);
  if (fromSession !== null) return fromSession;
  const fromLocal = localStorage.getItem(key);
  if (fromLocal !== null) sessionStorage.setItem(key, fromLocal);
  return fromLocal;
}
const recordar = () => (sessionStorage.getItem(RECORDAR_KEY) ?? localStorage.getItem(RECORDAR_KEY) ?? '1') === '1';

export const tokenStore = {
  getAccess: () => getWithSeed(ACCESS_KEY),
  getRefresh: () => getWithSeed(REFRESH_KEY),
  /** recordarDispositivo: solo al iniciar sesión; los refresh posteriores conservan la elección. */
  set: (access: string, refresh?: string, recordarDispositivo?: boolean) => {
    if (recordarDispositivo !== undefined) {
      const v = recordarDispositivo ? '1' : '0';
      sessionStorage.setItem(RECORDAR_KEY, v);
      if (recordarDispositivo) localStorage.setItem(RECORDAR_KEY, v); else localStorage.removeItem(RECORDAR_KEY);
    }
    const persistir = recordar();
    sessionStorage.setItem(ACCESS_KEY, access);
    if (persistir) localStorage.setItem(ACCESS_KEY, access); else localStorage.removeItem(ACCESS_KEY);   // semilla para pestañas nuevas
    if (refresh) {
      sessionStorage.setItem(REFRESH_KEY, refresh);
      if (persistir) localStorage.setItem(REFRESH_KEY, refresh); else localStorage.removeItem(REFRESH_KEY);
    }
  },
  /** ¿La sesión se conserva en el dispositivo? (también decide si se guarda el usuario en caché) */
  recordar,
  clear: () => {
    sessionStorage.removeItem(ACCESS_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(RECORDAR_KEY);
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(RECORDAR_KEY);
  },
};

export const apiClient: AxiosInstance = axios.create({
  baseURL: POSTGREST_URL,
  timeout: 90000,
  headers: { 'Content-Type': 'application/json' },
});

// -----------------------------------------------------
// Request: adjunta Bearer y traduce el atajo /rest/{schema}.{table}
// a la convención de PostgREST (/{table} + header Accept-Profile/Content-Profile).
// Esto evita reescribir todas las llamadas del CRUD dinámico.
// -----------------------------------------------------
apiClient.interceptors.request.use(
  (config) => {
    const token = tokenStore.getAccess();
    if (token) config.headers.set('Authorization', `Bearer ${token}`);

    const url = config.url || '';
    if (url.startsWith('/rest/')) {
      const rest = url.slice('/rest/'.length);
      const qIndex = rest.indexOf('?');
      const tableWithSchema = qIndex >= 0 ? rest.slice(0, qIndex) : rest;
      const query = qIndex >= 0 ? rest.slice(qIndex) : '';

      let schema = 'autenticacion';
      let table = tableWithSchema;
      if (tableWithSchema.includes('.')) {
        const parts = tableWithSchema.split('.');
        schema = parts[0];
        table = parts.slice(1).join('.');
      }

      config.url = `/${table}${query}`;
      config.headers.set('Accept-Profile', schema);
      const method = (config.method || 'get').toLowerCase();
      if (['post', 'patch', 'put', 'delete'].includes(method)) {
        config.headers.set('Content-Profile', schema);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// -----------------------------------------------------
// Response: refresh transparente del access token vía /rpc/refresh.
// -----------------------------------------------------
type RetriableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

let isRefreshing = false;
let pendingQueue: Array<{ resolve: () => void; reject: (err: unknown) => void }> = [];

function flushQueue(error: unknown) {
  pendingQueue.forEach((p) => (error ? p.reject(error) : p.resolve()));
  pendingQueue = [];
}

function isAuthRpc(url = ''): boolean {
  return url.includes('/rpc/login') || url.includes('/rpc/refresh') || url.includes('/rpc/register');
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableRequest | undefined;
    const status = error.response?.status;

    if (status === 401 && originalRequest && !originalRequest._retry && !isAuthRpc(originalRequest.url)) {
      originalRequest._retry = true;
      const refreshToken = tokenStore.getRefresh();

      if (!refreshToken) {
        tokenStore.clear();
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('auth:expired'));
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise<void>((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        }).then(() => apiClient(originalRequest));
      }

      isRefreshing = true;
      try {
        // axios "crudo" para no reentrar al interceptor ni adjuntar el Bearer expirado
        const { data } = await axios.post(
          `${POSTGREST_URL}/rpc/refresh`,
          { _refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json', 'Accept-Profile': 'autenticacion' } }
        );
        tokenStore.set(data.token, data.refreshToken);
        flushQueue(null);
        return apiClient(originalRequest);
      } catch (refreshError) {
        flushQueue(refreshError);
        // Sin red (obra sin señal, corte): NO se cierra la sesión — el refresh token sigue
        // valiendo y se reintenta al volver la conexión. Solo un rechazo real del servidor la cierra.
        const re = refreshError as { code?: string; message?: string };
        if (re?.code === 'ERR_NETWORK' || re?.message === 'Network Error') return Promise.reject(refreshError);
        tokenStore.clear();
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('auth:expired'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (!error.response && error.request) {
      console.error('Error de red - No hay respuesta del servidor');
    }
    return Promise.reject(error);
  }
);

// Helper para manejar errores de API
/**
 * ¿El error dice que el módulo no existe en ESTA instalación? (esquema no expuesto → 406
 * PGRST106, función inexistente → 404 PGRST202). TrackConstruccion comparte el frontend
 * con el ERP pero no tiene, p. ej., el módulo médico ni el asistente de voz.
 */
export function esModuloAusente(error: unknown): boolean {
  const r = (error as { response?: { status?: number; data?: { code?: string } } } | null)?.response;
  return !!r && (r.data?.code === 'PGRST106' || r.data?.code === 'PGRST202');
}

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return (
      error.response?.data?.message ||
      (error.response?.data as any)?.hint ||
      error.message ||
      'Error desconocido'
    );
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Error desconocido';
}
